import { useState, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
	Save,
	Settings as SettingsIcon,
	Building2,
	DollarSign,
	Receipt,
	Shield,
	Package,
	FileText,
	RefreshCw,
} from 'lucide-react'

import { useAuth } from '../../providers/AuthProvider'
import { useToast } from '../../components/ui/Toast'
import Button from '../../components/ui/Button'
import Input from '../../components/ui/Input'
import { PageHeader } from '../../components/ui/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import Checkbox from '../../components/ui/Checkbox'
import Label from '../../components/ui/Label'
import { useSettings, useBulkUpdateSettings, type Setting } from '../../hooks/useSettings'

interface SettingsGroup {
	category: string
	icon: React.ReactNode
	settings: Setting[]
}

export default function SettingsPage() {
	const { t } = useTranslation()
	const { hasPermission } = useAuth()
	const { toast } = useToast()

	const [changedSettings, setChangedSettings] = useState<Record<string, any>>({})

	const { data: settingsResponse, isLoading, refetch } = useSettings()
	const bulkUpdateMutation = useBulkUpdateSettings()

	const settings = settingsResponse?.data || []

	const handleSettingChange = useCallback((key: string, value: any) => {
		setChangedSettings((prev) => ({ ...prev, [key]: value }))
	}, [])

	const handleSave = useCallback(async () => {
		if (Object.keys(changedSettings).length === 0) {
			toast.info(t('settings.noChanges', 'No changes to save'))
			return
		}

		try {
			const settingsToUpdate = Object.entries(changedSettings).map(([key, value]) => ({
				key,
				value,
			}))
			await bulkUpdateMutation.mutateAsync(settingsToUpdate)
			toast.success(t('settings.saveSuccess', 'Settings saved successfully'))
			setChangedSettings({})
			await refetch()
		} catch (error) {
			const message =
				error instanceof Error ? error.message : t('errors.generic', 'An unexpected error occurred')
			toast.error(t('common.error', 'Error'), message)
		}
	}, [changedSettings, bulkUpdateMutation, toast, t, refetch])

	const handleReset = useCallback(() => {
		if (window.confirm(t('settings.confirmReset', 'Are you sure you want to reset all changes?'))) {
			setChangedSettings({})
		}
	}, [t])

	const getSettingValue = useCallback(
		(key: string): any => {
			if (changedSettings[key] !== undefined) {
				return changedSettings[key]
			}
			const setting = settings.find((s) => s.key === key)
			return setting?.value ?? ''
		},
		[changedSettings, settings],
	)

	const groupedSettings: SettingsGroup[] = useMemo(
		() =>
			[
				{
					category: 'company',
					icon: <Building2 className="h-5 w-5" />,
					settings: settings.filter((s) => s.category === 'company'),
				},
				{
					category: 'currency',
					icon: <DollarSign className="h-5 w-5" />,
					settings: settings.filter((s) => s.category === 'currency'),
				},
				{
					category: 'tax',
					icon: <Receipt className="h-5 w-5" />,
					settings: settings.filter((s) => s.category === 'tax'),
				},
				{
					category: 'inventory',
					icon: <Package className="h-5 w-5" />,
					settings: settings.filter((s) => s.category === 'inventory'),
				},
				{
					category: 'invoice',
					icon: <FileText className="h-5 w-5" />,
					settings: settings.filter((s) => s.category === 'invoice'),
				},
				{
					category: 'security',
					icon: <Shield className="h-5 w-5" />,
					settings: settings.filter((s) => s.category === 'security'),
				},
			].filter((group) => group.settings.length > 0),
		[settings],
	)

	const hasChanges = Object.keys(changedSettings).length > 0

	if (isLoading) {
		return (
			<div className="flex items-center justify-center min-h-[400px]" role="status" aria-live="polite">
				<LoadingSpinner size="lg" />
			</div>
		)
	}

	return (
		<div className="space-y-6">
			<PageHeader
				title={t('settings.title', 'Settings')}
				subtitle={t('settings.subtitle', 'Manage system settings')}
				actions={
					hasPermission('settings:update') && (
						<div className="flex gap-2">
							{hasChanges && (
								<Button variant="outline" onClick={handleReset} leftIcon={<RefreshCw className="h-4 w-4" />}>
									{t('settings.reset', 'Reset')}
								</Button>
							)}
							{hasChanges && (
								<Button
									onClick={handleSave}
									isLoading={bulkUpdateMutation.isPending}
									leftIcon={<Save className="h-4 w-4" />}
								>
									{t('settings.save', 'Save Changes')}
								</Button>
							)}
						</div>
					)
				}
			/>

			{groupedSettings.length === 0 ? (
				<Card>
					<CardContent className="p-12 text-center">
						<SettingsIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
						<h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
							{t('settings.noSettings', 'No settings available')}
						</h3>
						<p className="text-sm text-gray-500 dark:text-gray-400">
							{t('settings.noSettingsDescription', 'Settings will appear here once configured.')}
						</p>
					</CardContent>
				</Card>
			) : (
				<div className="space-y-6">
					{groupedSettings.map((group) => (
						<Card key={group.category}>
							<CardHeader>
								<CardTitle className="flex items-center gap-2">
									{group.icon}
									{t(`settings.categories.${group.category}`, group.category)}
								</CardTitle>
							</CardHeader>
							<CardContent className="space-y-6">
								{group.settings.map((setting) => {
									const value = getSettingValue(setting.key)
									const hasChanged = changedSettings[setting.key] !== undefined
									const isSystem = setting.isSystem

									return (
										<div
											key={setting.key}
											className={`p-4 rounded-lg border ${
												hasChanged
													? 'border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20'
													: 'border-gray-200 dark:border-gray-700'
											}`}
										>
											<div className="flex items-start justify-between mb-2">
												<Label htmlFor={setting.key} className="text-sm font-medium">
													{setting.description || setting.key}
												</Label>
												<div className="flex gap-2">
													{hasChanged && (
														<span className="text-xs text-blue-600 dark:text-blue-400 font-medium">
															{t('settings.changed', 'Changed')}
														</span>
													)}
													{isSystem && (
														<span className="text-xs text-gray-500 dark:text-gray-400">
															{t('settings.system', 'System')}
														</span>
													)}
												</div>
											</div>
											{typeof value === 'boolean' ? (
												<Checkbox
													id={setting.key}
													checked={value || false}
													onCheckedChange={(checked) => handleSettingChange(setting.key, checked)}
													disabled={!hasPermission('settings:update') || isSystem}
													label={
														value
															? t('common.yes', 'Yes')
															: t('common.no', 'No')
													}
												/>
											) : typeof value === 'number' ? (
												<Input
													id={setting.key}
													type="number"
													value={value}
													onChange={(e) =>
														handleSettingChange(setting.key, parseFloat(e.target.value) || 0)
													}
													disabled={!hasPermission('settings:update') || isSystem}
												/>
											) : Array.isArray(value) ? (
												<div className="space-y-2">
													<Input
														id={setting.key}
														value={value.join(', ')}
														onChange={(e) =>
															handleSettingChange(
																setting.key,
																e.target.value.split(',').map((v) => v.trim()),
															)
														}
														disabled={!hasPermission('settings:update') || isSystem}
														placeholder={t('settings.commaSeparated', 'Comma-separated values')}
													/>
													<p className="text-xs text-gray-500 dark:text-gray-400">
														{t('settings.arrayHint', 'Enter values separated by commas')}
													</p>
												</div>
											) : (
												<Input
													id={setting.key}
													value={value || ''}
													onChange={(e) => handleSettingChange(setting.key, e.target.value)}
													disabled={!hasPermission('settings:update') || isSystem}
												/>
											)}
										</div>
									)
								})}
							</CardContent>
						</Card>
					))}
				</div>
			)}
		</div>
	)
}
